/**
 * @tyndo/hooks — typed query/mutation hooks for the predictive drift + closed-
 * loop surface (`GET /api/v1/predictive/drift`,
 * `/api/v1/predictive/closed-loop/actions`; see
 * `Tyndo.Module.Predictive/Endpoints/PredictiveEndpoints.DriftMonitors.cs` +
 * `.cs` closed-loop maps).
 *
 * The Drift Response Workflow reads both feeds and enacts pending closed-loop
 * actions; retraining requests/cancels are driven by the retraining hooks in
 * `usePredictiveModels`. Cache invalidation is tenant-scoped via
 * `invalidateApiQueries`. Predictive-ui views consume these hooks only — no
 * direct fetch.
 */

import { useApiQuery, useApiMutation } from './useApi.ts';
import { useInvalidatePredictive } from './usePredictiveModels.ts';

import {
  type DriftReading,
  type ClosedLoopAction,
  type PredictiveListEnvelope,
  type EnactActionResult,
} from './usePredictiveTypes.ts';

export const PREDICTIVE_DRIFT_ENDPOINT = '/predictive/drift';
export const PREDICTIVE_CLOSED_LOOP_ACTIONS_ENDPOINT = '/predictive/closed-loop/actions';

// ── Reads ─────────────────────────────────────────────────────────────────

/** Drift readings in the current window (bare array or provenance envelope). */
export function usePredictiveDriftReadings() {
  return useApiQuery<DriftReading[] | PredictiveListEnvelope<DriftReading>>(
    PREDICTIVE_DRIFT_ENDPOINT,
  );
}

/** Closed-loop action queue (bare array or provenance envelope). */
export function usePredictiveClosedLoopActions() {
  return useApiQuery<ClosedLoopAction[] | PredictiveListEnvelope<ClosedLoopAction>>(
    PREDICTIVE_CLOSED_LOOP_ACTIONS_ENDPOINT,
  );
}

// ── Writes ────────────────────────────────────────────────────────────────

/**
 * Enact a pending closed-loop action
 * (POST /predictive/closed-loop/actions/{id}/enact). Returns the enact result
 * (`success` / `status` / `createdId` / `failureReason`); on success the action
 * queue prefix is invalidated so the queue refreshes.
 */
export function useEnactClosedLoopAction() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<EnactActionResult>();
  const mutateAsync = async (actionId: string): Promise<EnactActionResult> => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_CLOSED_LOOP_ACTIONS_ENDPOINT}/${encodeURIComponent(actionId)}/enact`,
      data: {},
    });
    invalidate(PREDICTIVE_CLOSED_LOOP_ACTIONS_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}
